import { Handle, Position } from "@xyflow/react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import CanvasIcon from "./CanvasIcon";
import { CHECK_LEVELS, worstLevel } from "./step-checks";
import { stepLook } from "./step-look";
import { describeStep, STEP_TONES } from "./step-summary";

const StatusNode = ({ data, sourcePosition, targetPosition }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const look = stepLook(data.nodeType);
  const checks = data.checks || [];
  const level = worstLevel(checks);
  const problems = checks
    .map((item) => t(`CANVAS_EDITOR.CHECKS.${item.code}`, item.params))
    .join("\n");

  return (
    <div
      className={classNames("canvas-node", {
        [`canvas-node--sim-${data.simulation}`]: data.simulation,
      })}
    >
      <Handle type="target" position={targetPosition} />
      {data.isStart ? (
        <span className="canvas-node__marker canvas-node__marker--start">
          {t("CANVAS_EDITOR.STEPS.START")}
        </span>
      ) : null}
      <span
        className={classNames(
          "canvas-node__icon",
          `canvas-tone--${look.tone}`,
          `canvas-tone-fill--${look.tone}`,
        )}
      >
        <CanvasIcon path={look.icon} />
      </span>
      <span className="canvas-node__text">
        <span className="canvas-node__title">
          {data.name || t("CANVAS_EDITOR.STEPS.UNNAMED")}
        </span>
        <span className="canvas-node__sub">
          <span
            className={`canvas-node__dot canvas-node__dot--${data.tone || STEP_TONES.MUTED}`}
          />
          <span className="canvas-node__sub-text">
            {describeStep({ data }, t)}
          </span>
        </span>
      </span>
      {level ? (
        <span
          className={classNames("canvas-node__issues", {
            "canvas-node__issues--warning": level === CHECK_LEVELS.WARNING,
          })}
          title={problems}
          aria-label={t("CANVAS_EDITOR.CHECKS.COUNT", { count: checks.length })}
        >
          {checks.length}
        </span>
      ) : null}
      {data.isEnd ? (
        <span className="canvas-node__marker canvas-node__marker--end">
          {t("CANVAS_EDITOR.STEPS.END")}
        </span>
      ) : null}
      <Handle type="source" position={sourcePosition} />
    </div>
  );
};

StatusNode.propTypes = {
  data: PropTypes.object.isRequired,
  sourcePosition: PropTypes.string,
  targetPosition: PropTypes.string,
};

StatusNode.defaultProps = {
  sourcePosition: Position.Bottom,
  targetPosition: Position.Top,
};

export default StatusNode;
